import {
  defaultPricingContent,
  defaultAcceleratorContent,
  defaultSiteSettings,
  type AcceleratorContent,
  type PricingContent,
  type SiteSettings,
} from '../config/siteContent';
import { courseCatalogs, type CourseCatalog } from '../data/courseDetails';
import { BACKEND_URL } from '../config/api';

function normalizeSettings(data?: Partial<SiteSettings> | null): SiteSettings {
  return {
    ...defaultSiteSettings,
    ...data,
    contact: {
      ...defaultSiteSettings.contact,
      ...(data?.contact ?? {}),
      officeHours: data?.contact?.officeHours?.length
        ? data.contact.officeHours
        : defaultSiteSettings.contact.officeHours,
    },
    intake: {
      ...defaultSiteSettings.intake,
      ...(data?.intake ?? {}),
    },
    navLinks: data?.navLinks?.length ? data.navLinks : defaultSiteSettings.navLinks,
    socials: data?.socials?.length ? data.socials : defaultSiteSettings.socials,
    stats: data?.stats?.length ? data.stats : defaultSiteSettings.stats,
    homePrograms: data?.homePrograms?.length ? data.homePrograms : defaultSiteSettings.homePrograms,
    aboutValues: data?.aboutValues?.length ? data.aboutValues : defaultSiteSettings.aboutValues,
    classFormats: data?.classFormats?.length ? data.classFormats : defaultSiteSettings.classFormats,
  };
}

function normalizePricing(data?: Partial<PricingContent> | null): PricingContent {
  const tiers = (data?.tiers?.length ? data.tiers : defaultPricingContent.tiers).map((tier, index) => {
    const fallbackTier = defaultPricingContent.tiers[index] ?? defaultPricingContent.tiers[0];
    return {
      ...fallbackTier,
      ...tier,
      price: {
        ...fallbackTier.price,
        ...(tier.price ?? {}),
      },
      features: tier.features?.length ? tier.features : fallbackTier.features,
      benefits: tier.benefits?.length ? tier.benefits : fallbackTier.benefits,
      courseContent: tier.courseContent?.length ? tier.courseContent : fallbackTier.courseContent,
      learningOutcomes: tier.learningOutcomes?.length ? tier.learningOutcomes : fallbackTier.learningOutcomes,
      professionalSkills: tier.professionalSkills?.length ? tier.professionalSkills : fallbackTier.professionalSkills,
      careerPreparation: tier.careerPreparation?.length ? tier.careerPreparation : fallbackTier.careerPreparation,
    };
  });

  return {
    ...defaultPricingContent,
    ...data,
    tiers,
    whoItsFor: data?.whoItsFor?.length ? data.whoItsFor : defaultPricingContent.whoItsFor,
    proofStats: data?.proofStats?.length ? data.proofStats : defaultPricingContent.proofStats,
    proofMessage: data?.proofMessage || defaultPricingContent.proofMessage,
    discounts: {
      ...defaultPricingContent.discounts,
      ...(data?.discounts ?? {}),
    },
    faqs: data?.faqs?.length ? data.faqs : defaultPricingContent.faqs,
  };
}

function normalizePrograms(data?: CourseCatalog[] | null): CourseCatalog[] {
  const source = Array.isArray(data) && data.length > 0 ? data : courseCatalogs;

  return source.map((program, index) => {
    const fallback = courseCatalogs[index] ?? courseCatalogs[0];

    return {
      ...fallback,
      ...program,
      price: {
        ...fallback.price,
        ...(program.price ?? {}),
      },
      objectives: program.objectives?.length ? program.objectives : fallback.objectives,
      prerequisites: program.prerequisites?.length ? program.prerequisites : fallback.prerequisites,
      curriculum: program.curriculum?.length ? program.curriculum : fallback.curriculum,
      classPeriods: program.classPeriods?.length ? program.classPeriods : fallback.classPeriods,
      tools: program.tools?.length ? program.tools : fallback.tools,
      benefits: program.benefits?.length ? program.benefits : fallback.benefits,
      parentInfo: program.parentInfo || fallback.parentInfo,
    };
  });
}

function normalizeAccelerator(data?: Partial<AcceleratorContent> | null): AcceleratorContent {
  return {
    ...defaultAcceleratorContent,
    ...data,
    hero: {
      ...defaultAcceleratorContent.hero,
      ...(data?.hero ?? {}),
    },
    offer: {
      ...defaultAcceleratorContent.offer,
      ...(data?.offer ?? {}),
      priceUgx: Number(data?.offer?.priceUgx ?? defaultAcceleratorContent.offer.priceUgx) || defaultAcceleratorContent.offer.priceUgx,
      priceUsd: Number(data?.offer?.priceUsd ?? defaultAcceleratorContent.offer.priceUsd) || defaultAcceleratorContent.offer.priceUsd,
    },
    introCards: data?.introCards?.length ? data.introCards : defaultAcceleratorContent.introCards,
    bestFor: {
      ...defaultAcceleratorContent.bestFor,
      ...(data?.bestFor ?? {}),
      items: data?.bestFor?.items?.length ? data.bestFor.items : defaultAcceleratorContent.bestFor.items,
    },
    whatStaysSame: {
      ...defaultAcceleratorContent.whatStaysSame,
      ...(data?.whatStaysSame ?? {}),
      items: data?.whatStaysSame?.items?.length ? data.whatStaysSame.items : defaultAcceleratorContent.whatStaysSame.items,
    },
    howItWorks: {
      ...defaultAcceleratorContent.howItWorks,
      ...(data?.howItWorks ?? {}),
      steps: data?.howItWorks?.steps?.length ? data.howItWorks.steps : defaultAcceleratorContent.howItWorks.steps,
    },
    guidance: {
      ...defaultAcceleratorContent.guidance,
      ...(data?.guidance ?? {}),
    },
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const siteContentApi = {
  async getSettings(): Promise<SiteSettings> {
    try {
      const result = await request<{ success: boolean; data: SiteSettings }>('/api/site/settings');
      return normalizeSettings(result.data);
    } catch {
      return defaultSiteSettings;
    }
  },

  async getPricing(): Promise<PricingContent> {
    try {
      const result = await request<{ success: boolean; data: PricingContent }>('/api/site/pricing');
      return normalizePricing(result.data);
    } catch {
      return defaultPricingContent;
    }
  },

  async updateSettings(token: string, data: SiteSettings): Promise<SiteSettings> {
    const result = await request<{ success: boolean; data: SiteSettings }>('/api/site/admin/settings', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return normalizeSettings(result.data);
  },

  async updatePricing(token: string, data: PricingContent): Promise<PricingContent> {
    const result = await request<{ success: boolean; data: PricingContent }>('/api/site/admin/pricing', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return normalizePricing(result.data);
  },

  async getPrograms(): Promise<CourseCatalog[]> {
    try {
      const result = await request<{ success: boolean; data: CourseCatalog[] }>('/api/site/programs');
      return normalizePrograms(result.data);
    } catch {
      return courseCatalogs;
    }
  },

  async getAccelerator(): Promise<AcceleratorContent> {
    try {
      const result = await request<{ success: boolean; data: AcceleratorContent }>('/api/site/accelerator');
      return normalizeAccelerator(result.data);
    } catch {
      return defaultAcceleratorContent;
    }
  },

  async updatePrograms(token: string, data: CourseCatalog[]): Promise<CourseCatalog[]> {
    const result = await request<{ success: boolean; data: CourseCatalog[] }>('/api/site/admin/programs', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return normalizePrograms(result.data);
  },

  async updateAccelerator(token: string, data: AcceleratorContent): Promise<AcceleratorContent> {
    const result = await request<{ success: boolean; data: AcceleratorContent }>('/api/site/admin/accelerator', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return normalizeAccelerator(result.data);
  },
};
